import React, { useState, useMemo } from 'react';
import { X, Check, Loader2, AlertCircle } from 'lucide-react';

export type ParkingTypeFilterMode = 'all' | 'include' | 'exclude';

export interface BulkParkingTypeFilter {
    mode: ParkingTypeFilterMode;
    types: string[];
}

interface BulkEditPanelProps {
    selectedLots: Array<{ lotId: string; parking_type?: string }>;
    isSaving: boolean;
    error: string | null;
    onApply: (patch: { displayInApp: boolean }, typeFilter: BulkParkingTypeFilter) => void;
    onClearSelection: () => void;
    onExit: () => void;
}

const PARKING_TYPES = [
    { value: 'free', label: 'Gratis' },
    { value: 'paid', label: 'Betalt' },
    { value: 'limited_time', label: 'Tidsbegrænset' },
];

const BulkEditPanel: React.FC<BulkEditPanelProps> = ({
    selectedLots,
    isSaving,
    error,
    onApply,
    onClearSelection,
    onExit,
}) => {
    const [displayInApp, setDisplayInApp] = useState<boolean | null>(null);
    const [filterMode, setFilterMode] = useState<ParkingTypeFilterMode>('all');
    const [filterTypes, setFilterTypes] = useState<Set<string>>(new Set());

    const toggleFilterType = (type: string) => {
        setFilterTypes((prev) => {
            const next = new Set(prev);
            if (next.has(type)) next.delete(type);
            else next.add(type);
            return next;
        });
    };

    const effectiveCount = useMemo(() => {
        if (filterMode === 'all') return selectedLots.length;
        const types: string[] = [];
        filterTypes.forEach((t) => types.push(t));
        if (types.length === 0) return filterMode === 'include' ? 0 : selectedLots.length;
        return selectedLots.filter((lot) => {
            const pt = lot.parking_type ?? 'free';
            return filterMode === 'include' ? types.includes(pt) : !types.includes(pt);
        }).length;
    }, [selectedLots, filterMode, filterTypes]);

    const handleApply = () => {
        if (displayInApp === null) return;
        const types: string[] = [];
        filterTypes.forEach((t) => types.push(t));
        onApply({ displayInApp }, { mode: filterMode, types });
    };

    const showTypeSelector = filterMode !== 'all';

    return (
        <div className="absolute bottom-4 right-4 z-30 bg-white border border-gray-200 rounded-xl shadow-xl p-4 w-80">
            <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-gray-900">
                    {selectedLots.length} {selectedLots.length === 1 ? 'plads valgt' : 'pladser valgt'}
                </span>
                <button
                    onClick={onExit}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    title="Afslut markerings-mode"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>

            {selectedLots.length === 0 ? (
                <p className="text-xs text-gray-500">
                    Træk et område på kortet for at vælge pladser.
                </p>
            ) : (
                <>
                    {/* Parking type filter */}
                    <div className="mb-4">
                        <p className="text-xs font-medium text-gray-700 mb-2">Parkeringstype filter</p>
                        <div className="flex gap-1 mb-2">
                            {(['all', 'include', 'exclude'] as ParkingTypeFilterMode[]).map((mode) => (
                                <button
                                    key={mode}
                                    type="button"
                                    onClick={() => setFilterMode(mode)}
                                    className={`flex-1 py-1 rounded text-xs font-medium border transition-colors ${
                                        filterMode === mode
                                            ? 'bg-gray-800 text-white border-gray-800'
                                            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    {mode === 'all' ? 'Alle' : mode === 'include' ? 'Kun' : 'Ekskluder'}
                                </button>
                            ))}
                        </div>

                        {showTypeSelector && (
                            <div className="flex gap-1.5 flex-wrap">
                                {PARKING_TYPES.map(({ value, label }) => {
                                    const active = filterTypes.has(value);
                                    return (
                                        <button
                                            key={value}
                                            type="button"
                                            onClick={() => toggleFilterType(value)}
                                            className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${
                                                active
                                                    ? 'bg-blue-600 text-white border-blue-600'
                                                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                            }`}
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {filterMode !== 'all' && (
                            <p className="text-xs text-gray-500 mt-1.5">
                                {effectiveCount} pladser påvirkes
                            </p>
                        )}
                    </div>

                    {/* displayInApp toggle */}
                    <div className="mb-4">
                        <p className="text-xs font-medium text-gray-700 mb-2">Synlig i app</p>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setDisplayInApp(true)}
                                className={`flex-1 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                                    displayInApp === true
                                        ? 'bg-green-600 text-white border-green-600'
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                Ja
                            </button>
                            <button
                                type="button"
                                onClick={() => setDisplayInApp(false)}
                                className={`flex-1 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                                    displayInApp === false
                                        ? 'bg-red-600 text-white border-red-600'
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                Nej
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-3 text-xs">
                            <AlertCircle className="h-4 w-4 flex-shrink-0" />
                            {error}
                        </div>
                    )}

                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={onClearSelection}
                            disabled={isSaving}
                            className="flex-1 py-1.5 rounded-md text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                        >
                            Nyt valg
                        </button>
                        <button
                            type="button"
                            onClick={handleApply}
                            disabled={isSaving || displayInApp === null || effectiveCount === 0}
                            className="flex-1 py-1.5 rounded-md text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 flex items-center justify-center gap-1.5 transition-colors"
                        >
                            {isSaving ? (
                                <>
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    Gemmer...
                                </>
                            ) : (
                                <>
                                    <Check className="h-3.5 w-3.5" />
                                    Anvend ({effectiveCount})
                                </>
                            )}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default BulkEditPanel;
